import { useFetcher } from "@website/fetcher/use-fetcher";
import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon, MaterialNameType } from "@website/icons/material";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { InfoChips } from "@website/ui/info-chips";
import { LazyImage } from "@website/ui/lazy-image";
import { numberConvert } from "@website/utils/number-convert";
import {
  CheckboxItemEventTypes,
  FiltersSchemaType
} from "@website/widget/filter";
import mapValues from "lodash-es/mapValues";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { handleTrackingEvent } from "../../map/index.tracking";
import ApartmentIcon from "./assets/apartmentIcon.svg";
import BeachfrontIcon from "./assets/beachfrontIcon.svg";
import CottageIcon from "./assets/cottageIcon.svg";
import EcotourismIcon from "./assets/ecotourismIcon.svg";
import MountainousIcon from "./assets/mountainousIcon.svg";
import NearbyIcon from "./assets/nearbyIcon.svg";
import VillaIcon from "./assets/villaIcon.svg";
import WithPoolIcon from "./assets/withPoolIcon.svg";
import WoodlandIcon from "./assets/woodlandIcon.svg";
import { fetchPropertyDynamicFilters } from "./index.server";
import { handleCategoryBadgeFilterClicked } from "./index.tracking";

export const ratesInfoChips = {
  "4.5": {
    text: "عالی",
    color: "success"
  },
  "4": {
    text: "خیلی خوب",
    color: "success"
  },
  "3.5": {
    text: "خوب",
    color: "default"
  }
};

const villaPropertyCategoryIconBySlug = {
  apartment: ApartmentIcon,
  around: NearbyIcon,
  beach: BeachfrontIcon,
  cottage: CottageIcon,
  ecotourism: EcotourismIcon,
  economy: VillaIcon,
  jungle: WoodlandIcon,
  luxury: VillaIcon,
  mountain: MountainousIcon,
  pool: WithPoolIcon,
  suite: VillaIcon,
  villa: VillaIcon
};

export const getVillaPropertyCategoryIcon = (
  name?: string
): (typeof villaPropertyCategoryIconBySlug)[keyof typeof villaPropertyCategoryIconBySlug] => {
  const normalizedSlug = String(name || "").toLowerCase();

  if (normalizedSlug.includes("pool")) return WithPoolIcon;
  if (normalizedSlug.includes("ecotourism")) return EcotourismIcon;
  if (normalizedSlug.includes("cottage")) return CottageIcon;
  if (normalizedSlug.includes("jungle")) return WoodlandIcon;
  if (normalizedSlug.includes("beach")) return BeachfrontIcon;
  if (normalizedSlug.includes("around")) return NearbyIcon;
  if (normalizedSlug.includes("mountain")) return MountainousIcon;
  if (normalizedSlug.includes("apartment")) return ApartmentIcon;
  if (normalizedSlug.includes("villa")) return VillaIcon;

  return villaPropertyCategoryIconBySlug[normalizedSlug] || VillaIcon;
};

export const useVillaFiltersSchema = (
  isListingDataFetched: boolean,
  cityPersianTitle: string,
  isNewPlP?: boolean
): {
  hasNearby: boolean;
  isFilterFetching: boolean;
  filtersSchema: FiltersSchemaType;
  chipsFiltersSchema: FiltersSchemaType;
  trackingCallback({ removed }: { removed: object }): void;
} => {
  const router = useRouter();
  const { isMobile } = useViewPort();
  const {
    data,
    isFetching: isFilterFetching,
    isLoading: isFilterLoading
  } = useFetcher({
    enabled: isListingDataFetched,
    queryKey: [
      "villa_dynamic_filters",
      String(router.query["locationType"]),
      String(router.query["cityTitle"]),
      String(router?.query?.["date_from"]),
      String(router?.query?.["date_to"]),
      Boolean(router.query["nearby"])
    ],
    queryFn: () =>
      fetchPropertyDynamicFilters(
        String(router.query["locationType"]),
        String(router.query["cityTitle"]),
        String(router?.query?.["date_from"]),
        String(router?.query?.["date_to"]),
        false,
        Boolean(router?.query?.["nearby"])
      ).then((data) => data)
  });

  const onChangeCategoryBadge = (item: CheckboxItemEventTypes) => {
    handleCategoryBadgeFilterClicked({
      item_id: item.name,
      item: item.label,
      isChecked: item.isChecked,
      city_id: Number(router?.query?.["cityTitle"]),
      date_from: toString(router.query?.["date_from"]),
      date_to: toString(router.query?.["date_to"])
    });
  };

  const chipsFiltersSchema: FiltersSchemaType = {
    ...(isNewPlP && data?.propertyCategories?.length > 0
      ? {
          property_categories: {
            icon: (
              <MaterialIcon
                className="text-on-surface-medium-emphasis text-headline-6"
                name="home_work"
                variant="outlined"
              />
            ),
            type: "strip" as const,
            title: isMobile ? "دسته‌بندی" : "دسته‌بندی اقامتگاه",
            options:
              data?.propertyCategories?.map((category) => ({
                name: String(category.id),
                label: category.title,
                icon: (
                  <LazyImage
                    alt=""
                    className="size-[30px]"
                    height={30}
                    src={getVillaPropertyCategoryIcon(category.slug).src}
                    width={30}
                  />
                )
              })) || []
          }
        }
      : {}),
    ...(data?.facilities?.length > 0
      ? {
          facilities: {
            icon: (
              <MaterialIcon
                variant="outlined"
                name="bento"
                className="text-on-surface-medium-emphasis text-headline-6"
              />
            ),
            type: "checkbox",
            title: "امکانات اقامتگاه",
            collapsible: isNewPlP ? false : data?.facilities.length >= 5,
            containerClassName:
              "overflow-auto max-h-[80vh] grid grid-cols-2 gap-x-4",
            options: data?.facilities?.map((facility) => ({
              name: String(facility?.id),
              label: facility?.title,
              icon: (
                <MaterialIcon
                  className="text-on-surface-medium-emphasis text-headline-5"
                  name={
                    facility.icon === "what.ico" || !facility.icon
                      ? "check_box"
                      : (facility.icon as MaterialNameType)
                  }
                  variant="outlined"
                />
              )
            }))
          }
        }
      : {}),
    price_range: {
      icon: (
        <MaterialIcon
          variant="outlined"
          name="payments"
          className="text-on-surface-medium-emphasis text-headline-6"
        />
      ),
      title: "قیمت",
      description: "مجموع قیمت",
      type: "range",
      min_key: "price_from",
      max_key: "price_to",
      step: 100_000,
      min: data?.priceRange?.min,
      max: data?.priceRange?.max
    },
    ...(!isNewPlP && data?.accommodations?.length > 0
      ? {
          type: {
            type: "checkbox",
            title: "نوع اقامتگاه",
            isNew: true,
            collapsible: data?.accommodations?.length > 5,
            options:
              data?.accommodations?.map((accommodation) => ({
                name: accommodation.type,
                label: accommodation.label
              })) || []
          }
        }
      : {}),
    ...(!isNewPlP && data?.instant
      ? {
          categories: {
            type: "checkbox",
            title: "پیشنهادات ویژه",
            onChange: onChangeCategoryBadge,
            options: [
              {
                label: "رزرو آنی",
                name: "instant"
              },
              ...(data?.categories?.map((category) => ({
                name: String(category.id),
                label: category.title
              })) || [])
            ]
          }
        }
      : {}),
    ...(isNewPlP && data?.rates.length > 0
      ? {
          rate: {
            icon: (
              <MaterialIcon
                variant="outlined"
                name="star_border"
                className="text-on-surface-medium-emphasis text-headline-6"
              />
            ),
            type: "radio" as const,
            title: "امتیاز کاربران",
            defaultValue: "all",
            items: [
              { value: "all", label: "همه" },
              ...(data?.rates || []).map((rate) => ({
                value: rate === 0 ? "new" : String(rate),
                icon: ratesInfoChips[String(rate)] ? (
                  <InfoChips
                    StartIcon={
                      <MaterialIcon className="text-body-2" name="stars" />
                    }
                    color={ratesInfoChips[String(rate)]?.color || ""}
                    size="medium"
                    text={ratesInfoChips[String(rate)]?.text || ""}
                    variant="round-accent"
                  />
                ) : undefined,
                label:
                  rate === 0
                    ? "اقامتگاه‌های بدون امتیاز"
                    : `${numberConvert(rate, {
                        locale: router.locale
                      })}+ امتیاز`
              }))
            ]
          }
        }
      : {}),
    ...(data?.instant || data?.categories?.length > 0
      ? {
          categories: {
            ...(isNewPlP
              ? {
                  icon: (
                    <MaterialIcon
                      variant="outlined"
                      name="local_offer"
                      className="text-on-surface-medium-emphasis text-headline-6"
                    />
                  )
                }
              : {}),
            type: "checkbox",
            title: "پیشنهادات ویژه",
            onChange: onChangeCategoryBadge,
            options: [
              {
                label: "رزرو آنی",
                name: "instant"
              },
              ...(data?.categories?.map((category) => ({
                name: String(category.id),
                label: numberConvert(category.title, { locale: router?.locale })
              })) || [])
            ]
          }
        }
      : {}),
    ...(isNewPlP && data?.nearby
      ? {
          nearby: {
            icon: (
              <MaterialIcon
                variant="outlined"
                name="location_on"
                className="text-on-surface-medium-emphasis text-headline-6"
              />
            ),
            type: "text" as const,
            title: `اقامتگاه‌های اطراف  ${cityPersianTitle}`
          }
        }
      : {})
  };

  return {
    filtersSchema: {
      q: {
        type: "input",
        title: "جستجو بر اساس نام اقامتگاه"
      },
      price_range: {
        title: "قیمت",
        description: "مجموع قیمت",
        type: "range" as const,
        min_key: "price_from",
        max_key: "price_to",
        step: 100_000,
        min: data?.priceRange?.min,
        max: data?.priceRange?.max
      },
      ...(data?.accommodations?.length > 0
        ? {
            type: {
              type: "checkbox" as const,
              title: "نوع اقامتگاه",
              hasSearch: false,
              collapsible: data?.accommodations?.length > 5,
              options:
                data?.accommodations?.map((accommodation) => ({
                  name: accommodation.type,
                  label: accommodation.label
                })) || []
            }
          }
        : {}),
      ...(data?.rates.length > 0
        ? {
            rate: {
              type: "radio",
              title: "امتیاز کاربران",
              defaultValue: "all",
              items: [
                { value: "all", label: "همه" },
                ...(data?.rates || []).map((rate) => ({
                  value: rate === 0 ? "new" : String(rate),
                  icon: ratesInfoChips[String(rate)] ? (
                    <InfoChips
                      StartIcon={
                        <MaterialIcon className="text-body-2" name="stars" />
                      }
                      color={ratesInfoChips[String(rate)]?.color || ""}
                      size="medium"
                      text={ratesInfoChips[String(rate)]?.text || ""}
                      variant="round-accent"
                    />
                  ) : undefined,
                  label:
                    rate === 0
                      ? "اقامتگاه‌های بدون امتیاز"
                      : `${numberConvert(rate, {
                          locale: router.locale
                        })}+ امتیاز`
                }))
              ]
            }
          }
        : {}),
      ...(data?.nearby
        ? {
            nearby: {
              type: "switch",
              title: "نمایش ویلاهای اطراف"
            }
          }
        : {}),
      ...(data?.geo_property_summary?.length > 1 &&
      (router.query?.locationType === "state" ||
        router.query?.locationType === "city" ||
        router.query?.locationType === "category")
        ? {
            geo_property_summary: {
              type: "checkbox",
              title: "انتخاب شهر",
              isNew: true,
              collapsible: data?.geo_property_summary.length > 5,
              options: data?.geo_property_summary?.map((geo_property) => ({
                name: `${String(geo_property?.geo_slug)}___${String(
                  geo_property?.geo_type
                )}___${router.query?.["nearby"] ?? false}`,
                label: geo_property?.title
                // icon: (
                //   <InfoChips
                //     color={"dim"}
                //     size="medium"
                //     text={`${numberConvert(geo_property?.property_count, {
                //       locale: router.locale
                //     })} اقامتگاه`}
                //     variant="text"
                //   />
                // )
              }))
            }
          }
        : {}),
      ...(data?.categories?.length > 0
        ? {
            categories: {
              type: "checkbox" as const,
              title: "پیشنهادات ویژه",
              onChange: onChangeCategoryBadge,
              options: [
                ...(data?.instant
                  ? [
                      {
                        label: "رزرو آنی",
                        name: "instant"
                      }
                    ]
                  : []),
                ...(data?.categories?.map((category) => ({
                  name: String(category.id),
                  label: category.title
                })) || [])
              ]
            }
          }
        : {}),
      ...(data?.facilities?.length > 0
        ? {
            facilities: {
              type: "checkbox",
              title: "امکانات اقامتگاه",
              collapsible: data?.facilities.length > 5,
              options: data?.facilities?.map((facility) => ({
                name: String(facility?.id),
                label: facility?.title,
                icon: (
                  <MaterialIcon
                    className="text-on-surface-medium-emphasis text-headline-5"
                    name={
                      facility.icon === "what.ico" || !facility.icon
                        ? "check_box"
                        : (facility.icon as MaterialNameType)
                    }
                    variant="outlined"
                  />
                )
              }))
            }
          }
        : {}),
      ...(isNewPlP && data?.propertyCategories?.length > 0
        ? {
            property_categories: {
              type: "checkbox" as const,
              title: "دسته‌بندی اقامتگاه",
              hasSearch: false,
              collapsible: data?.propertyCategories?.length > 5,
              options:
                data?.propertyCategories?.map((category) => ({
                  name: String(category.id),
                  label: category.title
                })) || []
            }
          }
        : {})
    },
    hasNearby: data?.nearby,
    chipsFiltersSchema,
    isFilterFetching:
      !data && (!isListingDataFetched || isFilterFetching || isFilterLoading),
    trackingCallback: ({ removed }: { removed: object }) => {
      mapValues(removed, (value, key) =>
        handleTrackingEvent(USER_TRACKING_EVENTS.FILTER_PILL_REMOVED, {
          removed_filter_name: key,
          removed_filter_value: value
        })
      );
    }
  };
};
